<template>
  <PxStickyContainer
    v-if="!hideHelpBar"
    position="bottom"
    transition="slide-y"
    :delay="1"
    class="min-h-16 rounded-t-3xl border-t-4 border-sky-800 bg-white px-2 py-1.5 sm:px-5"
  >
    <div
      class="mx-auto flex w-full max-w-[60rem] items-center gap-2 max-[350px]:flex-col"
      :class="showSavedCompare && showRightButton ? 'justify-between' : 'justify-center'"
    >
      <div v-if="showSavedCompare" class="flex w-fit gap-2 xs:gap-3">
        <div class="relative">
          <HpButton
            icon="heart-outline"
            variant="secondary"
            @click="show('global-saved-plans-sidebar')"
            class="h-11 !min-h-0 w-20 !rounded-full !border !border-border !bg-gray-200/50 sm:w-40 sm:min-w-36 [&_svg]:mr-1"
          >
            <span class="hidden sm:flex">Saved</span>
            <span
              v-if="savedCount > 0"
              class="relative grid size-6 shrink-0 place-items-center rounded-full bg-sky-800 font-semibold text-white sm:ml-1.5"
            >
              <span
                v-if="isSavedPinging"
                class="absolute -z-10 size-6 animate-ping rounded-full bg-sky-900"
              ></span>
              {{ savedCount }}
            </span>
          </HpButton>
          <Transition
            enter-active-class="transition duration-150 cubic-bezier(0.99, 1.865, 1.965, 1)"
            enter-from-class="opacity-0 translate-y-1 scale-0"
            enter-to-class="opacity-100 translate-y-0 scale-105"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-1"
          >
            <aside v-show="savedTooltip.show" class="tooltip w-24 md:w-36">
              {{ savedTooltip.text }}
            </aside>
          </Transition>
        </div>

        <div class="relative">
          <HpButton
            icon="bookmark"
            variant="secondary"
            @click="onCompareStickyFooter"
            class="h-11 !min-h-0 w-20 !rounded-full !border !border-border !bg-gray-200/50 sm:w-40 [&_svg]:mr-0.5"
          >
            <span class="hidden sm:flex">Compare</span>
            <span
              v-if="comparedCount > 0"
              class="relative grid size-6 shrink-0 place-items-center rounded-full bg-sky-800 font-semibold text-white sm:ml-1.5"
            >
              <span
                v-if="isComparePinging"
                class="absolute -z-10 size-6 animate-ping rounded-full bg-sky-900"
              ></span>
              {{ comparedCount }}
            </span>
          </HpButton>
          <Transition
            enter-active-class="transition duration-150 cubic-bezier(0.99, 1.865, 1.965, 1)"
            enter-from-class="opacity-0 translate-y-1 scale-0"
            enter-to-class="opacity-100 translate-y-0 scale-105"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-1"
          >
            <aside v-show="compareTooltip.show" class="tooltip w-24 md:w-48">
              {{ compareTooltip.text }}
            </aside>
          </Transition>
        </div>
      </div>

      <PxPushToCsReviewButton v-if="showPushToCsNotChat" />

      <template v-else>
        <HpButton
          v-if="!hideChatLauncher && !isHideCsChat"
          id="sticky-help-btn"
          class="relative w-fit !rounded-full !bg-sky-900 !px-3 !text-white"
          @click="onRequestChat"
          variant="minimal"
        >
          <aside
            class="absolute -top-4 right-0 flex size-7 items-center justify-center rounded-full border border-white bg-danger text-center font-semibold text-white"
            v-if="unreadMessages > 0"
          >
            {{ unreadMessages }}
          </aside>
          <span class="relative mr-2.5">
            <HpIcon name="message" class="-mr-1" />
            <span
              v-if="isSupportHours"
              class="online-indicator absolute -right-2.5 -top-1 size-3.5 rounded-full border-2 border-sky-900 bg-[#2AFF09]"
            ></span>
          </span>
          <template v-if="flowChatLabel">
            <span class="hidden xs:flex">{{ content?.data?.flowChatButtonLabel?.desktop }}</span>
            <span class="flex xs:hidden">{{ content?.data?.flowChatButtonLabel?.mobile }}</span>
          </template>

          <template v-else>
            <span class="hidden sm:flex">{{ content?.data?.quotesChatButtonLabel?.desktop }} </span>
            <span class="flex sm:hidden">{{ content?.data?.quotesChatButtonLabel?.mobile }}</span>
          </template>
        </HpButton>
      </template>
    </div>
    <PxSavedPlansSidebar id="global-saved-plans-sidebar" />
  </PxStickyContainer>
</template>

<script lang="ts" setup>
  import { EngineStore } from '~/stores/engine'
  import { ProfileStore } from '~/stores/profile'
  import { SessionStore } from '~/stores/session'
  import { PlanType } from '~/generated/api-clients-generated'

  const { getComponentContent } = Cms.use()
  const { content } = await getComponentContent('StickyGlobalFooter')
  const { isHideCsChat } = PdpPushToCsConfig.use()
  const { show } = DialogController.use()
  const { push } = AppRouter.use()
  const { path } = AppRoute.use()
  const { flag } = FeatureFlags.use()
  const { isSupportHours } = await SupportHours.use()
  const { sortBy } = QuoteUtils.useSort()
  const profile = ProfileStore.use()
  const session = SessionStore.use()
  const engine = EngineStore.use()
  const hideChatLauncher = computed(() => flag<boolean>('hide-chat-launcher').value)
  const pushToCsReview = computed(() => flag<string[]>('push-to-cs-review').value)

  const usePing = (source: Ref<number>) => {
    const isPinging = ref(false)
    watch(source, (newVal, oldVal) => {
      if (newVal > oldVal) {
        isPinging.value = true
        setTimeout(() => (isPinging.value = false), 1000)
      }
    })
    return isPinging
  }

  const planType = computed(() => engine.selectedPlanType)
  const isMapd = computed(() => planType.value === PlanType.MAPD)
  const { unreadMessages } = PxChat.use()

  const savedCount = computed(
    () => profile.savedPlans[session.planYear ?? new Date().getFullYear()]?.length ?? 0
  )

  const comparedCount = computed(
    () =>
      session.comparedPlans.filter((p) => engine.quotesCatalog[p]?.type === engine.selectedPlanType)
        .length
  )

  const topQuotes = computed(() =>
    _take(
      // @ts-ignore
      sortBy(engine.availableQuotes, 'default').map((x) => x.medicareId),
      3
    )
  )

  const isComparePinging = usePing(comparedCount)
  const isSavedPinging = usePing(savedCount)

  const flowChatLabel = computed(
    () =>
      !path.value.includes('/quotes') &&
      !path.value.includes('/details') &&
      !path.value.includes('/compare')
  )

  const showSavedCompare = computed(
    () =>
      path.value.includes('/quotes') ||
      path.value.includes('/details') ||
      path.value.includes('/compare')
  )

  const showPushToCsNotChat = computed(
    () =>
      isMapd.value &&
      ((path.value.includes('/details') && pushToCsReview.value.includes('details')) ||
        (path.value.includes('/compare') && pushToCsReview.value.includes('compare')))
  )

  const showRightButton = computed(
    () => showPushToCsNotChat.value || (!hideChatLauncher.value && !isHideCsChat.value)
  )

  const hideHelpBarPaths = computed(
    () => path.value == '/dashboard' || path.value == '/standby' || path.value == '/confirm'
  )

  const hideHelpBar = computed(
    () =>
      hideHelpBarPaths.value ||
      (flowChatLabel.value && hideChatLauncher.value) ||
      (!showSavedCompare.value && !showRightButton.value)
  )

  const makeTooltip = () => {
    const show = ref(false)
    const text = ref('')
    let timeout: ReturnType<typeof setTimeout> | null = null
    const showFor = (msg: string, ms: number) => {
      if (timeout) clearTimeout(timeout)
      text.value = msg
      show.value = true
      timeout = setTimeout(() => {
        show.value = false
        timeout = null
      }, ms)
    }
    const clear = () => {
      if (timeout) clearTimeout(timeout)
    }
    return reactive({ show, text, showFor, clear })
  }

  const compareTooltip = makeTooltip()
  const savedTooltip = makeTooltip()

  const isMounted = ref(false)
  onMounted(() => (isMounted.value = true))
  onUnmounted(() => {
    compareTooltip.clear()
    savedTooltip.clear()
  })

  watch(savedCount, (newVal, oldVal) => {
    if (!isMounted.value) return
    if (newVal === 1 && oldVal === 0) {
      savedTooltip.showFor('Click here to see your saved plans.', 3000)
    }
  })

  watch(comparedCount, (newVal, oldVal) => {
    if (!isMounted.value) return
    if (newVal === 1 && oldVal === 0) {
      compareTooltip.showFor('Got it! Add one more plan to start comparing.', 4000)
      return
    }
    if (newVal === 2 && oldVal === 1) {
      compareTooltip.showFor('Nice! Click here to see your plans side-by-side.', 3000)
      return
    }
  })

  // check for changes to auto selected top 3 in event user adds docs/drugs and the top 3 change
  watch(topQuotes, (newTopQuotes, oldTopQuotes) => {
    if (!oldTopQuotes?.length || !newTopQuotes?.length) return
    const current = session.comparedPlans
    const wasAutoSelectedTopThree =
      current.length === oldTopQuotes.length && oldTopQuotes.every((id) => current.includes(id))
    if (wasAutoSelectedTopThree) {
      session.comparedPlans = [...newTopQuotes]
    }
  })

  const onCompareStickyFooter = () => {
    const { track } = Analytics.use()

    if (comparedCount.value === 0) {
      track('StickyFooterCompareClicked - Top Three')
      session.comparedPlans = topQuotes.value
      push('/compare')
      return
    }

    if (comparedCount.value === 1) {
      compareTooltip.showFor('Add at least one more plan to compare', 3000)
      return
    }

    track('StickyFooterCompareClicked')
    push('/compare')
  }

  const pxSource = computed(() => `stickyGlobalFooter:${path.value}`)

  const onRequestChat = () => {
    console.log(`launching chat from ${pxSource.value}`)
    PxChat.show({ pxSource: pxSource.value })
  }
</script>

<style scoped>
  .tooltip {
    @apply pointer-events-none absolute bottom-[107%] left-1/2 -translate-x-1/2 text-balance rounded-2xl border-2 border-white bg-sky-900 px-2 pb-3.5 pt-2.5 text-center text-base font-semibold leading-tight text-white shadow-tooltip;

    &::before {
      @apply absolute -bottom-3.5 left-1/2 -z-10 size-6 -translate-x-1/2 rotate-45 rounded-br-md border-b-2 border-r-2 border-white bg-sky-900;
      content: '';
    }
  }
</style>
